import { useTranslation } from '@wix/fe-essentials/react';
import { useQueryClient } from '@wix/fe-essentials/react-query/react';
import { isNotOptional } from '@wix/seo-common-core';
import { useEffect, useMemo, useState } from 'react';
import { BiCtaNames, BiModalTypes } from '../bi/types';
import { showFailedFetchRuleExecutionResultsModal } from '../components/modals/show-failed-fetch-rule-execution-results';
import { QueryId } from '../queries';
import { AnalysisState, usePanelStateAtom } from './rules-state';
import { useA11yWizardAdapter } from './use-a11y-wizard-adapter';
import { useBiLogger } from './use-bi-logger';
export const useQueriesManager = tpaPageIds => {
  const {
    a11yWizardAdapter,
    editorHost
  } = useA11yWizardAdapter();
  const {
    t
  } = useTranslation();
  const biLogger = useBiLogger();
  const queryClient = useQueryClient();
  const {
    getGroupData
  } = usePanelStateAtom();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isModalCancelled, setIsModalCancelled] = useState(false);
  const queryKeys = useMemo(() => tpaPageIds.map(tpaPageId => {
    const groupData = getGroupData(tpaPageId ?? '');
    const itemType = groupData == null ? void 0 : groupData.itemType;
    if (!itemType) {
      return null;
    }
    const queryKey = [QueryId.QueryRuleExecutionResultsByJob, itemType, tpaPageId];
    return queryKey;
  }).filter(isNotOptional), [tpaPageIds, getGroupData]);
  const queries = useMemo(() => {
    return queryKeys.map(queryKey => {
      const queryState = queryClient.getQueryState(queryKey);
      if (!queryState) {
        return null;
      }
      return {
        queryKey,
        queryState
      };
    }).filter(isNotOptional);
  }, [queryKeys, queryClient]);
  const allQueriesFinished = queries.every(query => query.queryState.isFetching === false);
  const isSomeAnalyzing = useMemo(() => tpaPageIds.some(tpaPageId => {
    const groupData = getGroupData(tpaPageId ?? '');
    const analysisState = groupData == null ? void 0 : groupData.analysisState;
    return analysisState === AnalysisState.Analyzing || analysisState === AnalysisState.FirstAnalyzing || analysisState === AnalysisState.AnalyzeDonePendingResults;
  }), [tpaPageIds, getGroupData]);
  useEffect(() => {
    if (isSomeAnalyzing && isModalCancelled) {
      setIsModalCancelled(false);
    }
  }, [isSomeAnalyzing, isModalCancelled]);
  const failedQueries = queries.filter(query => query.queryState.error);
  if (failedQueries.length > 0 && allQueriesFinished && !isModalOpen && !isSomeAnalyzing && !isModalCancelled) {
    setIsModalOpen(true);
    showFailedFetchRuleExecutionResultsModal({
      a11yWizardAdapter,
      biLogger,
      editorHost,
      errorMessage: t('accessibility-wizard.fetch-rule-execution-results.failed-to-fetch-results-modal.pages.content'),
      handleCancel: () => {
        biLogger.modalCtaClick({
          ctaName: BiCtaNames.Cancel,
          modalType: BiModalTypes.UnableToFetchRuleExecutionResults
        });
        handleBatchCancel();
      },
      handleTryAgain: () => refetchAllFailedQueries(),
      t,
      title: t('accessibility-wizard.fetch-rule-execution-results.failed-to-fetch-results-modal.pages.title')
    });
  }
  const refetchAllFailedQueries = () => {
    failedQueries.forEach(query => {
      queryClient.refetchQueries({
        queryKey: query.queryKey
      });
    });
    setIsModalOpen(false);
  };
  const handleBatchCancel = () => {
    setIsModalOpen(false);
    setIsModalCancelled(true);
  };
};
//# sourceMappingURL=use-queries-manager.js.map